/**
 * Authentication Context
 *
 * Provides global authentication state and actions throughout the app.
 * Handles user session, login, logout, and authentication status.
 *
 * D7: Emits 'token-refreshed' event via postMessage when JWT is refreshed,
 * allowing useRealtimeSocket to close and reconnect with new token.
 *
 * Impersonation: on first mount we look for a `?impersonation=` query
 * param; if present (or already in sessionStorage from a prior page in
 * this tab) we forward the token via Authorization header for every
 * request and surface the active context to UI consumers via
 * `useAuth().impersonation`.
 */

import { createContext, useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthState, LoginCredentials, CredentialLoginInput } from './types';
import { authService, purgeLegacyCachedUser } from './authService';
import {
  adoptImpersonationFromUrl,
  endImpersonation as endImpersonationSession,
  type ImpersonationContext,
} from './impersonation';

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithCredential: (input: CredentialLoginInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Active impersonation context, or null when not impersonating. */
  impersonation: ImpersonationContext | null;
  /** Tear down impersonation and reload. */
  endImpersonation: () => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication context.
 * Automatically restores the session from backend cookies on mount.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });
  const [impersonation, setImpersonation] = useState<ImpersonationContext | null>(null);

  // D7: Notify listeners when token is refreshed via postMessage
  const notifyTokenRefreshed = useCallback(() => {
    window.postMessage({ type: 'token-refreshed' }, window.location.origin);
  }, []);

  // Restore session on mount — impersonation FIRST (so the apiClient
  // immediately sends the bearer header), then a normal session restore
  // which now hits /me/ with that bearer.
  useEffect(() => {
    // Phase 2I (GDPR R12) — clear any pre-trim cached user from
    // existing browsers before restoreSession resolves. Existing
    // users carry an email + name + accessRoles tree in
    // localStorage from before this commit; wipe it on the next
    // page load so the legacy PII shape stops surfacing.
    purgeLegacyCachedUser();

    const restoreSession = async () => {
      // 1. Adopt impersonation from URL handoff code (exchanged to a
      //    real JWT server-side) or fall back to whatever is already in
      //    sessionStorage. Awaited so /me/ runs after the apiClient can
      //    inject the bearer header.
      const ctx = await adoptImpersonationFromUrl();
      setImpersonation(ctx);

      try {
        const session = await authService.restoreSession();
        if (session) {
          setAuthState({
            user: session.user,
            isAuthenticated: true,
            isLoading: false,
          });
          notifyTokenRefreshed();
          return;
        }
      } catch (error) {
        console.error('Session restoration failed:', error);
      }

      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    restoreSession();
  }, [notifyTokenRefreshed]);

  // Auto-end impersonation when the JWT expires.
  useEffect(() => {
    if (!impersonation) return;
    const remaining = new Date(impersonation.expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      endImpersonationSession();
      return;
    }
    const timer = window.setTimeout(() => endImpersonationSession(), remaining);
    return () => window.clearTimeout(timer);
  }, [impersonation]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const { user } = await authService.login(credentials);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      notifyTokenRefreshed();
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      throw error;
    }
  }, [notifyTokenRefreshed]);

  const loginWithCredential = useCallback(async (input: CredentialLoginInput) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const { user } = await authService.loginWithCredential(input);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
      notifyTokenRefreshed();
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      throw error;
    }
  }, [notifyTokenRefreshed]);

  const logout = useCallback(async () => {
    await authService.logout();
    // Clear all React Query caches so the next user session
    // doesn't see stale data from the previous user.
    queryClient.clear();
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    ...authState,
    login,
    loginWithCredential,
    logout,
    impersonation,
    endImpersonation: endImpersonationSession,
  }), [authState, login, loginWithCredential, logout, impersonation]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
